import { ReactNode, useState } from "react";
import { Info } from "lucide-react";
import { cn } from "cn";
import t from "@/lang/lang";
import { ServiceVariationSelectField } from "@/services/backend/omni/servicevariation/components/service-variation-select";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import { FieldErrors } from "@/shared/components/field/field";
import {
  DecimalNumberInputField,
  TextInputField,
} from "@/shared/components/field/input-field";
import { TextareaField } from "@/shared/components/field/textarea-field";
import {
  useFieldErrors,
  useHasFieldErrors,
} from "@/shared/lib/errors/err-validation-failed-context";
import { formatEuro } from "@/shared/lib/utilities/format-euro";
import {
  PositionSubscriptionIntervalSelectItems,
  subscriptionIntervalFromSelectValue,
  subscriptionIntervalToSelectValue,
} from "../types/position";
import { PositionKind } from "../types/position-kind";
import { PositionUnitSelectItems } from "../types/position-unit";
import { PositionCategorySelectItems } from "../types/position-category";
import {
  OptionAvailability,
  optionSubscriptionPrice,
  PositionBaseData,
  PositionBaseDataSubPosition,
} from "../types/position-base-data";
import { CheckboxField, Section, SelectField } from "./position-details-dialog";
import {
  PricesUsed,
  PricesUsedOf,
  priceUsedFromSelectValue,
  priceUsedSelectItems,
  priceUsedToSelectValue,
} from "../types/price-used";
import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import { DataSheetsField } from "@/services/backend/omni/datasheet/components/data-sheets-field";

/**
 * PositionOptionAvailabilityItem is a place an option may be offered at,
 * with its name for the user.
 */
export type PositionOptionAvailabilityItem = {
  value: OptionAvailability;
  label: string;
};

// Titles are added with their grouping, so they are no option.
export const positionKindSelectItems = [
  { value: PositionKind.Default, label: t("Position") },
  { value: PositionKind.Dynamic, label: t("Dynamisch") },
  { value: PositionKind.Text, label: t("Text") },
  { value: PositionKind.PriceModifierRelative, label: t("Rabatt (%)") },
  { value: PositionKind.PriceModifierAbsolute, label: t("Rabatt (€)") },
  { value: PositionKind.SubTotal, label: t("Zwischensumme") },
  { value: PositionKind.Total, label: t("Gesamtpreis") },
];

/**
 * PositionOptionDetailsDialog shows and edits all fields of an option.
 * Unlike the table, it edits a draft and hands it to onChange only on
 * confirm. fieldName is the key of the option, e.g. "options.0".
 * companyId and serviceKind are those of the base data: its service variations
 * and the data sheets of the company are offered.
 * pricesUsed are the prices a position may show, a rule of the service
 * kind.
 * Angeboten in is only shown with several availabilities.
 */
export function PositionOptionDetailsDialog({
  option,
  companyId,
  serviceKind,
  pricesUsed,
  availabilities,
  onChange,
  fieldName,
  disabled,
}: {
  option: PositionBaseData;
  companyId: string;
  serviceKind: string;
  pricesUsed: PricesUsedOf;
  availabilities: PositionOptionAvailabilityItem[];
  onChange: (option: PositionBaseData) => void;
  fieldName: string;
  disabled?: boolean;
}) {
  const key = (field: string) => `${fieldName}.${field}`;

  return (
    <DraftDialog
      value={option}
      title={option.label}
      onChange={onChange}
      fieldName={fieldName}
      disabled={disabled}
    >
      {(draft, change) => (
        <>
          <Section title={t("Angebot")}>
            <TextInputField
              label={t("Bezeichnung")}
              text={draft.label}
              onTextChange={(label) => change({ label })}
              fieldName={key("label")}
              debounce={false}
              disabled={disabled}
            />
            <SelectField
              label={t("Art")}
              items={positionKindSelectItems}
              value={draft.kind}
              onValueChange={(kind) => change({ kind })}
              fieldName={key("kind")}
              disabled={disabled}
            />
            <ServiceVariationSelectField
              label={t("Variation")}
              serviceKind={serviceKind}
              value={draft.serviceVariation}
              onValueChange={(serviceVariation) => change({ serviceVariation })}
              fieldName={key("serviceVariation")}
              disabled={disabled}
            />
            <DataSheetsField
              companyId={companyId}
              serviceKind={serviceKind}
              value={draft.dataSheetIds ?? []}
              onValueChange={(dataSheetIds) => change({ dataSheetIds })}
              fieldName={key("dataSheetIds")}
              disabled={disabled}
            />
            {availabilities.length > 1 && (
              <AvailabilityField
                availabilities={availabilities}
                values={draft.availableIn}
                onValuesChange={(availableIn) => change({ availableIn })}
                fieldName={key("availableIn")}
                disabled={disabled}
              />
            )}
            <CheckboxField
              label={t("Vorbelegt")}
              description={t(
                "Wird jedem neuen Leistungsverzeichnis hinzugefügt",
              )}
              checked={draft.seed}
              onCheckedChange={(seed) => change({ seed })}
              fieldName={key("seed")}
              disabled={disabled}
            />
          </Section>
          <OptionValueFields
            value={draft}
            onChange={change}
            prices={pricesUsed(draft.kind, draft.serviceVariation)}
            fieldName={fieldName}
            disabled={disabled}
          />
        </>
      )}
    </DraftDialog>
  );
}

/**
 * OptionSubPositionDetailsDialog shows and edits all fields of a
 * sub-position of an option, like {@link PositionOptionDetailsDialog}.
 * kinds are the kinds it may have. serviceVariation is the one of its option,
 * which the sub-position takes.
 */
export function OptionSubPositionDetailsDialog({
  subPosition,
  kinds,
  serviceVariation,
  pricesUsed,
  onChange,
  fieldName,
  disabled,
}: {
  subPosition: PositionBaseDataSubPosition;
  kinds: PositionKind[];
  serviceVariation: ServiceVariation;
  pricesUsed: PricesUsedOf;
  onChange: (subPosition: PositionBaseDataSubPosition) => void;
  fieldName: string;
  disabled?: boolean;
}) {
  return (
    <DraftDialog
      value={subPosition}
      title={subPosition.text || t("Unterposition")}
      onChange={onChange}
      fieldName={fieldName}
      disabled={disabled}
    >
      {(draft, change) => (
        <>
          <Section title={t("Unterposition")}>
            <SelectField
              label={t("Art")}
              items={positionKindSelectItems.filter((item) =>
                kinds.includes(item.value),
              )}
              value={draft.kind}
              onValueChange={(kind) => change({ kind })}
              fieldName={`${fieldName}.kind`}
              disabled={disabled}
            />
          </Section>
          <OptionValueFields
            value={draft}
            onChange={change}
            prices={pricesUsed(draft.kind, serviceVariation)}
            fieldName={fieldName}
            disabled={disabled}
          />
        </>
      )}
    </DraftDialog>
  );
}

/**
 * DraftDialog edits a draft of value, set when it opens, and hands it to
 * onChange on confirm. Its trigger is highlighted while the errors hold
 * keys of fieldName.
 */
function DraftDialog<T>({
  value,
  title,
  onChange,
  fieldName,
  disabled,
  children,
}: {
  value: T;
  title: string;
  onChange: (value: T) => void;
  fieldName: string;
  disabled?: boolean;
  children: (draft: T, change: (patch: Partial<T>) => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const { hasErrors } = useHasFieldErrors(fieldName);

  const openChange = (next: boolean) => {
    if (next) setDraft(value);
    setOpen(next);
  };
  const change = (patch: Partial<T>) =>
    setDraft((current) => ({ ...current, ...patch }));
  const confirm = () => {
    onChange(draft);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={openChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className={cn("w-8 rounded-none", hasErrors && "text-destructive")}
          aria-label={t("Details")}
          title={t("Details")}
        >
          <Info />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {children(draft, change)}
        <CommonDialogFooter
          isLoading={false}
          onClickAction={confirm}
          actionLabel={t("Übernehmen")}
          disabled={disabled}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * OptionValueFields are the fields an option and its sub-positions share:
 * the values of the position they add. prices are the prices it may show.
 */
function OptionValueFields<T extends PositionBaseDataSubPosition>({
  value,
  onChange,
  prices,
  fieldName,
  disabled,
}: {
  value: T;
  onChange: (patch: Partial<T>) => void;
  prices: PricesUsed;
  fieldName: string;
  disabled?: boolean;
}) {
  const key = (field: string) => `${fieldName}.${field}`;
  const change = (patch: Partial<PositionBaseDataSubPosition>) =>
    onChange(patch as Partial<T>);
  const subscriptionPrice = optionSubscriptionPrice(value);

  return (
    <>
      <Section title={t("Text")}>
        <TextInputField
          label={t("Produktnummer")}
          text={value.productNumber}
          onTextChange={(productNumber) => change({ productNumber })}
          fieldName={key("productNumber")}
          debounce={false}
          disabled={disabled}
        />
        <TextInputField
          label={t("Kurztext")}
          text={value.textShort}
          onTextChange={(textShort) => change({ textShort })}
          fieldName={key("textShort")}
          debounce={false}
          disabled={disabled}
        />
        <TextareaField
          label={t("Text")}
          value={value.text}
          onChange={(e) => change({ text: e.target.value })}
          fieldName={key("text")}
          debounce={false}
          disabled={disabled}
          fieldProps={{ className: "sm:col-span-2" }}
        />
      </Section>
      <Section title={t("Preis")}>
        <SelectField
          label={t("Kategorie")}
          items={PositionCategorySelectItems}
          value={value.category}
          onValueChange={(category) => change({ category })}
          fieldName={key("category")}
          disabled={disabled}
        />
        <SelectField
          label={t("Einheit")}
          items={PositionUnitSelectItems}
          value={value.unit}
          onValueChange={(unit) => change({ unit })}
          fieldName={key("unit")}
          disabled={disabled}
        />
        <DecimalNumberInputField
          label={t("Preis / Einheit")}
          value={value.unitPrice}
          onValueChange={(unitPrice) => change({ unitPrice })}
          precision={2}
          fieldName={key("unitPrice")}
          debounce={false}
          disabled={disabled}
        />
        <DecimalNumberInputField
          label={t("Anzahl")}
          value={value.count}
          onValueChange={(count) => change({ count })}
          fieldName={key("count")}
          debounce={false}
          disabled={disabled}
        />
        <DecimalNumberInputField
          label={t("Stunden")}
          value={value.hours}
          onValueChange={(hours) => change({ hours })}
          fieldName={key("hours")}
          debounce={false}
          disabled={disabled}
        />
        <DecimalNumberInputField
          label={t("Stundensatz")}
          value={value.hoursRate}
          onValueChange={(hoursRate) => change({ hoursRate })}
          precision={2}
          fieldName={key("hoursRate")}
          debounce={false}
          disabled={disabled}
        />
        {prices.allowed.length > 1 && (
          <SelectField
            label={t("Preis im Dokument")}
            items={priceUsedSelectItems(prices)}
            value={priceUsedToSelectValue(value.priceUsed)}
            onValueChange={(v) =>
              change({ priceUsed: priceUsedFromSelectValue(v) })
            }
            fieldName={key("priceUsed")}
            disabled={disabled}
          />
        )}
      </Section>
      <Section title={t("Abo")}>
        <DecimalNumberInputField
          label={t("Turnus (Wo.)")}
          value={value.cycle}
          onValueChange={(cycle) => change({ cycle })}
          precision={0}
          fieldName={key("cycle")}
          debounce={false}
          disabled={disabled}
        />
        <SelectField
          label={t("Abo-Intervall")}
          items={PositionSubscriptionIntervalSelectItems}
          value={subscriptionIntervalToSelectValue(value.subscriptionInterval)}
          onValueChange={(v) =>
            change({
              subscriptionInterval: subscriptionIntervalFromSelectValue(v),
            })
          }
          fieldName={key("subscriptionInterval")}
          disabled={disabled}
        />
        <TextInputField
          label={t("Abo-Preis")}
          text={
            subscriptionPrice === undefined ? "" : formatEuro(subscriptionPrice)
          }
          onTextChange={() => null}
          debounce={false}
          disabled
        />
      </Section>
    </>
  );
}

/**
 * AvailabilityField selects the places an option is offered at, one
 * checkbox each, in the order of availabilities.
 */
function AvailabilityField({
  availabilities,
  values,
  onValuesChange,
  fieldName,
  disabled,
}: {
  availabilities: PositionOptionAvailabilityItem[];
  values: OptionAvailability[];
  onValuesChange: (values: OptionAvailability[]) => void;
  fieldName: string;
  disabled?: boolean;
}) {
  const { errors, invalid, reset } = useFieldErrors(fieldName);

  const toggle = (value: OptionAvailability, checked: boolean) => {
    reset();
    const selected = checked
      ? [...values, value]
      : values.filter((v) => v !== value);
    onValuesChange(
      availabilities.map((a) => a.value).filter((v) => selected.includes(v)),
    );
  };

  return (
    <Field data-invalid={invalid || undefined} className="sm:col-span-2">
      <FieldLabel>{t("Angeboten in")}</FieldLabel>
      <div className="flex flex-wrap gap-4">
        {availabilities.map((a) => (
          <label key={a.value} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={values.includes(a.value)}
              onCheckedChange={(checked) => toggle(a.value, checked === true)}
              aria-invalid={invalid || undefined}
              disabled={disabled}
            />
            {a.label}
          </label>
        ))}
      </div>
      <FieldErrors errors={errors} />
    </Field>
  );
}
